import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api, SLUG } from "@/lib/api";

const SOURCE_LABEL = {
  ANONYMOUS_THOUGHT: "Anonyme Gedanken",
  GOOGLE_REVIEW: "Öffentliche Stimmen",
  ARTIST_INPUT: "Artist-Impulse",
};

export default function ClusterDrawer({ clusterId, onClose, onSelectCluster }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clusterId) return;
    setLoading(true);
    api
      .get(`/spaces/${SLUG}/clusters/${clusterId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [clusterId]);

  if (!clusterId) return null;

  return (
    <>
      <div onClick={onClose} style={overlay} />
      <div data-testid="cluster-detail-drawer" className="cs-fadein" style={drawer}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <div className="cs-mono" style={{ fontSize: 10, color: "var(--cs-mute)", marginBottom: 8 }}>
              SEMANTISCHES CLUSTER
            </div>
            <h2 className="cs-heading" style={{ fontSize: 22, color: "var(--cs-bone)", letterSpacing: "0.14em" }}>
              {data?.name || "…"}
            </h2>
          </div>
          <button data-testid="cluster-drawer-close-btn" onClick={onClose} style={closeBtn}>
            <X size={18} />
          </button>
        </div>

        {loading && <p className="cs-mono" style={{ fontSize: 11, color: "var(--cs-mute)" }}>LADE …</p>}

        {data && !loading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              <Metric label="AKTIVITÄT" value={data.activity_level} />
              <Metric label="GRÖSSE" value={`${data.size}`} />
              <Metric label="SPANNUNG" value={`${Math.round((data.tension || 0) * 100)}%`} />
            </div>

            <Section title="DOMINANTE THEMEN">
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {(data.concepts || []).map((c) => (
                  <span key={c} className="cs-mono" style={tag}>{c}</span>
                ))}
              </div>
            </Section>

            <Section title="QUELLEN-ZUSAMMENSETZUNG">
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {Object.entries(data.source_composition || {}).map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--cs-ash)" }}>
                    <span>{SOURCE_LABEL[k] || k}</span>
                    <span className="cs-mono">{v}</span>
                  </div>
                ))}
              </div>
            </Section>

            {data.fragments?.length > 0 && (
              <Section title="FREIGEGEBENE FRAGMENTE">
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {data.fragments.map((f, i) => (
                    <p key={i} style={{ fontSize: 13, color: "var(--cs-parchment)", lineHeight: 1.6, fontStyle: "italic", borderLeft: "2px solid rgba(216,158,58,0.4)", paddingLeft: 12 }}>
                      „{f.text}"
                    </p>
                  ))}
                </div>
              </Section>
            )}

            {data.related_clusters?.length > 0 && (
              <Section title="VERWANDTE CLUSTER">
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {data.related_clusters.map((r) => (
                    <button key={r.id} onClick={() => onSelectCluster({ id: r.id })} className="cs-mono" style={{ ...tag, cursor: "pointer" }}>
                      {r.name}
                    </button>
                  ))}
                </div>
              </Section>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="cs-mono" style={{ fontSize: 10, color: "var(--cs-mute)", marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}
function Metric({ label, value }) {
  return (
    <div>
      <div className="cs-mono" style={{ fontSize: 9, color: "var(--cs-mute)" }}>{label}</div>
      <div style={{ fontSize: 18, color: "var(--cs-bone)", marginTop: 4, fontFamily: "Cinzel, serif" }}>{value}</div>
    </div>
  );
}

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 40, backdropFilter: "blur(3px)" };
const drawer = {
  position: "fixed", top: 0, right: 0, height: "100%", width: "min(440px, 92vw)", zIndex: 41,
  background: "rgba(16,16,14,0.97)", borderLeft: "1px solid var(--cs-border)",
  padding: "34px 30px", overflowY: "auto",
};
const closeBtn = { background: "transparent", border: "1px solid var(--cs-border)", color: "var(--cs-ash)", width: 34, height: 34, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" };
const tag = { fontSize: 10, padding: "5px 10px", border: "1px solid var(--cs-border)", color: "var(--cs-parchment)", background: "rgba(6,6,5,0.5)" };
